import { useState, useCallback, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  Search, RotateCcw, Trash2, Phone, Mail, MailX,
} from 'lucide-react';
import { adminApi } from '../../../lib/api.js';
import type { JunkReviewItem } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/Input.js';
import { Select } from '../../../components/ui/Select.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useAuth } from '../../../lib/auth.js';
import { cn } from '../../../lib/cn.js';
import { LandlordDrawer } from '../../../components/landlords/LandlordDrawer.js';

export const Route = createFileRoute('/_auth/admin/junk-review')({
  component: JunkReviewPage,
});

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: 'archived', label: 'Archived' },
  { value: 'active', label: 'Active flagged' },
  { value: 'all', label: 'All' },
];

const FLAG_OPTIONS = [
  { value: '', label: 'All flags' },
  { value: 'name_is_building', label: 'Name is building' },
  { value: 'placeholder_name', label: 'Placeholder name' },
  { value: 'landline_only', label: 'Landline only' },
  { value: 'no_phone_no_email', label: 'No phone, no email' },
  { value: 'no_mobile_no_email', label: 'No mobile, no email' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function absoluteTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return parseISO(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(str: string | null, len = 40): string {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

// ── Row-level save state ─────────────────────────────────────────────────────

type RowState = 'idle' | 'saving' | 'saved';

// ── Main page ────────────────────────────────────────────────────────────────

function JunkReviewPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filters
  const [status, setStatus] = useState<'archived' | 'active' | 'all'>('archived');
  const [flag, setFlag] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search input
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((val: string) => {
    setQ(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQ(val);
      setPage(1);
    }, 300);
  }, []);

  // Selection
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Drawer
  const [drawerLandlordId, setDrawerLandlordId] = useState<number | null>(null);

  // Per-row save states
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});

  const setRowState = (id: number, state: RowState) => {
    setRowStates((prev) => ({ ...prev, [id]: state }));
    if (state === 'saved') {
      setTimeout(() => setRowStates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      }), 2000);
    }
  };

  // Query
  const queryKey = ['admin', 'junk-review', { status, flag, q: debouncedQ, page }];
  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: () => adminApi.junkReview({
      status,
      flag: flag || undefined,
      q: debouncedQ || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Invalidate and clear selection after mutations
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'junk-review'] });
    setSelected(new Set());
  };

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: (id: number) => adminApi.restore(id),
    onMutate: (id) => setRowState(id, 'saving'),
    onSuccess: (_data, id) => {
      setRowState(id, 'saved');
      invalidate();
      toast('Lead restored to active list', 'success');
    },
    onError: (err: Error, id) => {
      setRowState(id, 'idle');
      toast(err.message, 'error');
    },
  });

  // Hard delete mutation
  const hardDeleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.hardDelete(id),
    onMutate: (id) => setRowState(id, 'saving'),
    onSuccess: (_data, id) => {
      setRowState(id, 'idle');
      invalidate();
      toast('Lead permanently deleted', 'success');
    },
    onError: (err: Error, id) => {
      setRowState(id, 'idle');
      toast(err.message, 'error');
    },
  });

  // Bulk restore
  const bulkRestoreMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map((id) => adminApi.restore(id)));
    },
    onSuccess: () => {
      invalidate();
      toast(`${selected.size} leads restored`, 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  // Bulk hard delete
  const bulkHardDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map((id) => adminApi.hardDelete(id)));
    },
    onSuccess: () => {
      invalidate();
      toast(`${selected.size} leads permanently deleted`, 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const allVisibleIds = items.map((i) => i.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allVisibleIds));
    }
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRestore = (item: JunkReviewItem) => {
    if (!window.confirm(`Restore "${item.name}" back to the active list?`)) return;
    restoreMutation.mutate(item.id);
  };

  const handleHardDelete = (item: JunkReviewItem) => {
    if (!window.confirm(
      `Permanently delete "${item.name}"? This cannot be undone. A snapshot will be saved to the audit log.`
    )) return;
    hardDeleteMutation.mutate(item.id);
  };

  const handleBulkRestore = () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Restore ${ids.length} lead(s) back to the active list?`)) return;
    bulkRestoreMutation.mutate(ids);
  };

  const handleBulkHardDelete = () => {
    const ids = Array.from(selected);
    if (!window.confirm(
      `Permanently delete ${ids.length} lead(s)? This cannot be undone. Snapshots will be saved to the audit log.`
    )) return;
    bulkHardDeleteMutation.mutate(ids);
  };

  const handleFilterChange = (setter: (v: any) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
    setSelected(new Set());
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <h1 className="text-xl font-bold text-primary-text">Junk Review</h1>
        <p className="text-xs text-secondary mt-0.5">
          Leads marked for cleanup. Restore mistakes; hard-delete after 30 days.
        </p>
      </div>

      {/* Filter row */}
      <div className="px-4 sm:px-6 py-3 border-b border-border bg-[#f7f8fc] flex flex-wrap gap-2 items-center">
        <div className="min-w-[140px]">
          <Select
            aria-label="Status filter"
            options={STATUS_OPTIONS}
            value={status}
            onChange={handleFilterChange(setStatus)}
            className="!min-h-[36px] text-xs"
          />
        </div>
        <div className="min-w-[180px]">
          <Select
            aria-label="Flag filter"
            options={FLAG_OPTIONS}
            value={flag}
            onChange={handleFilterChange(setFlag)}
            className="!min-h-[36px] text-xs"
          />
        </div>
        <div className="flex-1 min-w-[180px] max-w-sm">
          <Input
            type="search"
            placeholder="Search name, unit, phone…"
            value={q}
            onChange={(e) => handleSearchChange(e.target.value)}
            icon={<Search className="w-4 h-4" />}
            aria-label="Search junk leads"
            className="!min-h-[36px] text-xs"
          />
        </div>
        <span className="text-xs text-secondary ml-auto whitespace-nowrap">
          {isFetching && !isLoading ? 'Refreshing…' : `${total} lead${total !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="px-4 sm:px-6 py-2 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-primary-text">
            Selected: {selected.size}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleBulkRestore}
            loading={bulkRestoreMutation.isPending}
            className="flex items-center gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Restore selected
          </Button>
          {isSuperAdmin && (
            <Button
              size="sm"
              variant="danger"
              onClick={handleBulkHardDelete}
              loading={bulkHardDeleteMutation.isPending}
              className="flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Hard delete selected
            </Button>
          )}
          <button
            className="ml-auto text-xs text-secondary hover:text-primary-text min-h-[36px] px-2"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {/* Desktop column headers */}
        <div className="hidden lg:grid grid-cols-[40px_minmax(0,2fr)_minmax(0,1.5fr)_80px_80px_60px_120px_120px_130px_90px_120px] px-4 py-2.5 bg-[#f7f8fc] border-b border-border text-[10px] font-semibold text-secondary uppercase tracking-wide sticky top-0 z-10">
          <span className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Select all visible rows"
              className="w-4 h-4 rounded border-border text-primary"
            />
          </span>
          <span>Lead name</span>
          <span>Building</span>
          <span>Top unit</span>
          <span>Phones</span>
          <span>Email</span>
          <span>Archived at</span>
          <span>Archived by</span>
          <span>Reason</span>
          <span>Days</span>
          <span className="text-right">Actions</span>
        </div>

        {/* Rows */}
        <div className="divide-y divide-border">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Trash2 className="w-10 h-10 text-secondary mx-auto mb-3 opacity-30" />
              <p className="text-sm text-secondary font-medium">No leads found</p>
              <p className="text-xs text-secondary mt-1">
                {debouncedQ || flag ? 'Try adjusting your filters' : 'No leads match the current status filter'}
              </p>
            </div>
          ) : (
            items.map((item) => (
              <JunkRow
                key={item.id}
                item={item}
                selected={selected.has(item.id)}
                onToggle={() => toggleRow(item.id)}
                rowState={rowStates[item.id] ?? 'idle'}
                isSuperAdmin={isSuperAdmin}
                onRestore={() => handleRestore(item)}
                onHardDelete={() => handleHardDelete(item)}
                onOpenDrawer={() => setDrawerLandlordId(item.id)}
                isRestoring={restoreMutation.isPending && restoreMutation.variables === item.id}
                isDeleting={hardDeleteMutation.isPending && hardDeleteMutation.variables === item.id}
              />
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 sm:px-6 py-3 border-t border-border flex items-center justify-between gap-4 flex-wrap">
          <span className="text-xs text-secondary">
            Page {page} of {totalPages} &middot; {total} total
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => { setPage((p) => p - 1); setSelected(new Set()); }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => { setPage((p) => p + 1); setSelected(new Set()); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Landlord drawer */}
      <LandlordDrawer
        landlordId={drawerLandlordId}
        onClose={() => setDrawerLandlordId(null)}
      />
    </div>
  );
}

// ── Row component ─────────────────────────────────────────────────────────────

interface JunkRowProps {
  item: JunkReviewItem;
  selected: boolean;
  onToggle: () => void;
  rowState: RowState;
  isSuperAdmin: boolean;
  onRestore: () => void;
  onHardDelete: () => void;
  onOpenDrawer: () => void;
  isRestoring: boolean;
  isDeleting: boolean;
}

function JunkRow({
  item,
  selected,
  onToggle,
  rowState,
  isSuperAdmin,
  onRestore,
  onHardDelete,
  onOpenDrawer,
  isRestoring,
  isDeleting,
}: JunkRowProps) {
  const isArchived = !!item.archivedAt;
  const daysClass = item.daysArchived >= 30 ? 'text-green-700 font-semibold' : 'text-red-600 font-semibold';

  const hardDeleteDisabled = !item.eligibleForHardDelete;
  const hardDeleteTooltip = hardDeleteDisabled
    ? `Hard delete locked — archived only ${item.daysArchived} day${item.daysArchived !== 1 ? 's' : ''} ago, requires 30+.`
    : undefined;

  return (
    <div
      className={cn(
        'group flex flex-col lg:grid lg:grid-cols-[40px_minmax(0,2fr)_minmax(0,1.5fr)_80px_80px_60px_120px_120px_130px_90px_120px] items-start lg:items-center gap-2 px-4 py-3 transition-colors',
        selected ? 'bg-blue-50' : 'hover:bg-hover-bg',
      )}
    >
      {/* Checkbox */}
      <div className="flex items-center justify-center pt-0.5 lg:pt-0">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${item.name}`}
          className="w-4 h-4 rounded border-border text-primary"
        />
      </div>

      {/* Lead name */}
      <div className="min-w-0">
        <button
          onClick={onOpenDrawer}
          className="text-sm font-medium text-primary hover:underline text-left truncate block max-w-full"
          aria-label={`Open drawer for ${item.name}`}
        >
          {item.name || <span className="text-secondary italic">Unnamed</span>}
        </button>
        {/* Mobile: show extra info inline */}
        <div className="lg:hidden flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-secondary">
          {item.building && <span>{item.building}</span>}
          {item.topUnitNo && <span>Unit {item.topUnitNo}</span>}
          <span className="flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {item.phoneCount}
          </span>
          {item.hasEmail ? (
            <span className="flex items-center gap-1 text-green-700"><Mail className="w-3 h-3" />Email</span>
          ) : (
            <span className="flex items-center gap-1 text-red-500"><MailX className="w-3 h-3" />No email</span>
          )}
          {item.archivedAt && (
            <span title={absoluteTime(item.archivedAt)}>
              {relativeTime(item.archivedAt)}
            </span>
          )}
          {item.archivedByName && <span>by {item.archivedByName}</span>}
          {item.archiveReason && (
            <span title={item.archiveReason || undefined} className="italic">
              {truncate(item.archiveReason, 30)}
            </span>
          )}
          <span className={daysClass}>{item.daysArchived}d</span>
        </div>
      </div>

      {/* Building — desktop only */}
      <div className="hidden lg:block min-w-0">
        <span className="text-sm text-secondary truncate block" title={item.building || undefined}>
          {item.building || '—'}
        </span>
      </div>

      {/* Top unit — desktop */}
      <div className="hidden lg:block">
        <span className="text-sm text-secondary">{item.topUnitNo || '—'}</span>
      </div>

      {/* Phones — desktop */}
      <div className="hidden lg:flex items-center gap-1">
        <Phone className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
        <span className="text-sm text-secondary">{item.phoneCount}</span>
      </div>

      {/* Email — desktop */}
      <div className="hidden lg:block">
        {item.hasEmail ? (
          <Mail className="w-4 h-4 text-green-600" aria-label="Has email" />
        ) : (
          <MailX className="w-4 h-4 text-red-400" aria-label="No email" />
        )}
      </div>

      {/* Archived at — desktop */}
      <div className="hidden lg:block min-w-0">
        {item.archivedAt ? (
          <span
            className="text-xs text-secondary truncate block"
            title={absoluteTime(item.archivedAt)}
          >
            {relativeTime(item.archivedAt)}
          </span>
        ) : (
          <span className="text-xs text-secondary">—</span>
        )}
      </div>

      {/* Archived by — desktop */}
      <div className="hidden lg:block min-w-0">
        <span className="text-xs text-secondary truncate block">{item.archivedByName || '—'}</span>
      </div>

      {/* Reason — desktop */}
      <div className="hidden lg:block min-w-0">
        <span
          className="text-xs text-secondary truncate block"
          title={item.archiveReason || undefined}
        >
          {truncate(item.archiveReason)}
        </span>
      </div>

      {/* Days — desktop */}
      <div className="hidden lg:block">
        <span className={cn('text-sm', daysClass)}>{item.daysArchived}d</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 justify-end">
        {/* Row saving state */}
        {rowState === 'saving' && (
          <span className="text-xs text-secondary mr-1">Saving…</span>
        )}
        {rowState === 'saved' && (
          <span className="text-xs text-green-600 mr-1">Saved</span>
        )}

        {/* Restore */}
        {isArchived && (
          <button
            onClick={onRestore}
            disabled={isRestoring || isDeleting}
            className={cn(
              'p-2 rounded-lg hover:bg-green-50 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors',
              (isRestoring || isDeleting) && 'opacity-50 cursor-not-allowed',
            )}
            aria-label={`Restore ${item.name}`}
            title="Restore to active list"
          >
            <RotateCcw className={cn('w-4 h-4 text-green-600', isRestoring && 'animate-spin')} />
          </button>
        )}

        {/* Hard delete — super_admin only */}
        {isSuperAdmin && (
          <span title={hardDeleteTooltip}>
            <button
              onClick={onHardDelete}
              disabled={hardDeleteDisabled || isRestoring || isDeleting}
              className={cn(
                'p-2 rounded-lg hover:bg-red-50 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors',
                (hardDeleteDisabled || isRestoring || isDeleting) && 'opacity-40 cursor-not-allowed',
              )}
              aria-label={`Permanently delete ${item.name}`}
              aria-disabled={hardDeleteDisabled}
            >
              <Trash2 className={cn('w-4 h-4 text-danger', isDeleting && 'animate-pulse')} />
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
